import {
  PersonDetailsDocument,
  PersonDetailsQuery,
  usePersonUpdateVisibleToMutation,
} from "gql/generated";
import { useState } from "react";
import { useLoggedInAs } from "shared/hooks/useLoggedInAs";
import { useToastContext } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { usePostHog } from "shared/hooks";

export type UseScratchPadProps = {
  person: PersonDetailsQuery["personDetails"];
};

export const useScratchPad = ({ person }: UseScratchPadProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_DETAILS_MODAL.SCRATCHPAD",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const loggedInAs = useLoggedInAs();
  const { trackPostHogException } = usePostHog();

  const [isEditing, setIsEditing] = useState(false);
  const [scratchpad, setScratchpad] = useState(person?.scratchpad ?? "");

  const [updateVisibleTo, { loading: updateVisibleToLoading }] =
    usePersonUpdateVisibleToMutation({
      refetchQueries: [PersonDetailsDocument],
    });

  /**
   * Save the scratchpad to the person
   * @returns void
   */
  const handleSave = async () => {
    if (!person || !loggedInAs) return;

    // If there is no change, just swap modes
    if (scratchpad === person.scratchpad) {
      setIsEditing(false);
      return;
    }

    try {
      const { errors } = await updateVisibleTo({
        variables: {
          id: person._id,
          entityId: loggedInAs.entityId,
          type: loggedInAs.type,
          scratchpad,
        },
      });
      if (errors) {
        throw new Error(errors[0].message);
      }
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("MESSAGES.SUCCESS"),
      });
      setIsEditing(false);
    } catch (error) {
      trackPostHogException(error, {
        fileName: "useScratchPad.ts",
        method: "handleSave",
        message: "Error saving scratchpad",
      });
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("MESSAGES.ERROR"),
      });
    }
  };

  return {
    isEditing,
    setIsEditing,
    scratchpad,
    setScratchpad,
    handleSave,
    isSaving: updateVisibleToLoading,
  };
};
